import { Box, Typography } from '@mui/material';
import { GridColumnVisibilityModel, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import searchStartImage from '@/assets/qflcsadvimage.png';
import ContactsTable from '@/components/ContactsTable';
import { environment } from '@/config';
import { SearchPreviewData } from '@/types';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { adjustPageNo, createBaseContactUrl, createRocketUrlForContact } from '@/util';
import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import SaveListDialog from '@components/SaveListDialog';
import { LIST_SIZE_LIMIT } from '@pages/QuickFilterListCreatorPage';
import { useAtomValue } from 'jotai';

const DEFAULT_PAGE_SIZE = 10;
const MAX_SEARCH_PREVIEW_SIZE = 1000; // TODO: Include in server payload?
const SUPPORTER_DATA_LOADING = 'Supporter data is loading';

type PreviewSectionProps = {
  isFetching: boolean;
  listPreview?: SearchPreviewData;
  selectedContactIds: number[];
  setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
  activeSearchTree: SearchTreeNode | undefined;
  setSortModel: Dispatch<SetStateAction<GridSortModel>>;
  sortModel: GridSortModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  filterModel: GridFilterModel;
  isSaveListDialogOpen: boolean;
  setIsSaveListDialogOpen: Dispatch<SetStateAction<boolean>>;
  onAllowNavigationBeforeLeave?: () => void;
  'data-testid'?: string;
};

export default function PreviewSection({
  isFetching,
  listPreview,
  selectedContactIds,
  setSelectedContactIds,

  activeSearchTree,
  setSortModel,
  sortModel,
  filterModel,
  setFilterModel,

  isSaveListDialogOpen,
  setIsSaveListDialogOpen,
  onAllowNavigationBeforeLeave,
  'data-testid': dataTestId,
}: PreviewSectionProps) {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState(0);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const domainCode = useAtomValue(domainCodeAtom);

  const isDev = ['test', 'local', 'dev'].includes(environment);
  const baseContactUrl = createBaseContactUrl(domainCode ?? 'ag', isDev);
  const contactIdUrl = useCallback(
    (id?: number) => createRocketUrlForContact(baseContactUrl, id),
    [baseContactUrl]
  );

  const previewCount = listPreview?.count ?? 0;
  const enablePicture = !listPreview && !isFetching;

  // [LIST-812]
  const disableSelectAll = (sortModel?.length ?? 0) > 0 || (filterModel?.items?.length ?? 0) > 0;

  return (
    <Box
      tabIndex={0}
      data-testid={dataTestId}
      sx={{
        flexGrow: 1,
        maxWidth: "2000px",
        margin: "auto",
        width: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        paddingTop: '16px',
        minWidth: 0
      }}>
      {enablePicture && (
        <Box sx={{ textAlign: 'center' }}>
          <p>
            <img src={searchStartImage} alt="This is the advanced search page." />
          </p>
          <Typography component="p" variant="h5">
            Please add some filters to build your search.
          </Typography>
        </Box>
      )}
      {!enablePicture && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              minWidth: 0
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                minWidth: 0,
                flexWrap: 'wrap',
                gap: 1
              }}>
              {isFetching ? (
                <>
                  <Typography>
                    <em>{SUPPORTER_DATA_LOADING}</em>&nbsp;...
                  </Typography>
                </>
              ) : (
                <Typography>
                  {previewCount} supporters found
                  {previewCount >= MAX_SEARCH_PREVIEW_SIZE && (
                    <span> (only first {MAX_SEARCH_PREVIEW_SIZE} shown)</span>
                  )}
                </Typography>
              )}
            </Box>
            <ContactsTable
              isLoading={isFetching}
              data={!isFetching ? (listPreview?.entities ?? []) : []}
              paginationModel={{ page: pageNo, pageSize }}
              onPaginationModelChange={(model) => {
                setPageSize(model.pageSize);
                setPageNo(
                  model.pageSize !== pageSize
                    ? adjustPageNo(pageSize, pageNo, model.pageSize)
                    : model.page
                );
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
              onSortModelChange={(model) => setSortModel(model)}
              sortModel={sortModel}
              onFilterModelChange={(model) => {
                setFilterModel(model);
              }}
              filterModel={filterModel}
              checkboxSelection
              rowSelectionModel={selectedContactIds}
              setSelectionModel={setSelectedContactIds}
              idUrl={contactIdUrl}
              disableSelectAll={disableSelectAll}
              idWithoutEmptyFilters
              filterPhoneOnFullNumber
            />
            <SaveListDialog
              isOpen={isSaveListDialogOpen}
              onClose={() => {
                setIsSaveListDialogOpen(false);
              }}
              contactIds={selectedContactIds.length === 0 ? undefined : selectedContactIds}
              tree={activeSearchTree}
              key={String(isSaveListDialogOpen)}
              isTruncateContacts={
                selectedContactIds.length > LIST_SIZE_LIMIT ||
                (selectedContactIds.length === 0 && (listPreview?.count ?? 0) > LIST_SIZE_LIMIT)
              }
              onAllowNavigationBeforeLeave={onAllowNavigationBeforeLeave}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
